import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import authService from '../services/authService';
import { BookOpen, LogOut, User, Loader2, AlertCircle } from 'lucide-react';

const CourseSelection = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadMyCourses();
  }, []);

  const loadMyCourses = async () => {
    try {
      setLoading(true);
      setError('');
      const userCourses = await authService.getMyCourses();
      setCourses(userCourses);
    } catch (err) {
      console.error('Errore caricamento corsi:', err);
      setError('Impossibile caricare i corsi. Riprova piu tardi.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      console.error('Errore logout:', err);
    }
  };

  return (
    <div className="min-h-screen bg-[#f3f5f8] p-4 sm:p-6">
      <div className="max-w-4xl mx-auto mb-6">
        <div className="bg-white rounded-2xl border border-[#dbe4f2] p-5 sm:p-6 shadow-[0_16px_40px_rgba(47,63,121,0.14)] flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-12 h-12 bg-[#2cb39a] rounded-xl flex items-center justify-center shadow-sm">
              <User className="w-6 h-6 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-bold text-[#31407a] truncate">{user?.name}</h2>
              <p className="text-sm text-[#4d5f91] truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="px-4 py-2 bg-[#d94b5a] text-white rounded-xl font-semibold hover:bg-[#c53f4d] transition shadow-sm flex items-center gap-2 whitespace-nowrap"
          >
            <LogOut className="w-4 h-4" />
            Esci
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl border border-[#dbe4f2] p-6 sm:p-8 shadow-[0_16px_40px_rgba(47,63,121,0.14)]">
          <div className="text-center mb-8">
            <img
              src="/favicon.png"
              alt="ALMA"
              className="w-14 h-14 mx-auto mb-4 rounded-xl shadow-sm"
            />
            <h1 className="text-3xl font-bold text-[#31407a] mb-2">I Miei Corsi</h1>
            <p className="text-[#4d5f91]">Seleziona un corso per continuare</p>
          </div>

          {loading && (
            <div className="text-center py-12">
              <Loader2 className="w-12 h-12 text-[#2cb39a] animate-spin mx-auto mb-4" />
              <p className="text-[#4d5f91]">Caricamento corsi...</p>
            </div>
          )}

          {error && !loading && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-red-800 mb-2">{error}</p>
                <button
                  onClick={loadMyCourses}
                  className="text-sm text-red-700 font-semibold hover:underline"
                >
                  Riprova
                </button>
              </div>
            </div>
          )}

          {!loading && !error && (
            <>
              {courses.length === 0 ? (
                <div className="text-center py-12">
                  <BookOpen className="w-16 h-16 text-[#9ca9c9] mx-auto mb-4" />
                  <p className="text-[#31407a] font-semibold mb-2">Non sei iscritto a nessun corso</p>
                  <p className="text-sm text-[#4d5f91]">
                    Contatta l&apos;amministratore per iscriverti a un corso
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {courses.map((course) => (
                    <button
                      key={course.id}
                      onClick={() => navigate(`/dashboard/${course.id}`)}
                      className="w-full px-6 py-4 bg-[#2cb39a] text-white rounded-xl font-semibold hover:bg-[#239a84] transition shadow-md flex items-center gap-3 text-left"
                    >
                      <BookOpen className="w-6 h-6 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-lg truncate">{course.title}</div>
                        {course.description && (
                          <div className="text-sm text-[#d9f6ef] mt-1 truncate">
                            {course.description}
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseSelection;
